import {
  BROKERAGE_KIND_LABELS,
  type Account,
  type BrokerageProposal,
  type BrokerageProposedLine,
  type ParsedBrokerageStatement,
} from '@accounting/shared';
import { AccountPicker } from '../accounts/AccountPicker';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

/**
 * The entries a brokerage statement calls for, before they are posted.
 *
 * The statement's "Change in NAV" block explains the month as components —
 * mark-to-market, dividends, interest (split by what it was), accrual changes,
 * commissions. Each becomes one entry against the investment account on the
 * statement date; after posting, the GL balance is the statement's ending
 * value. The owner confirms the offset account on every line here; the choice
 * is remembered on the account for next month.
 */
export function BrokeragePostingReview({
  brokerage,
  proposal,
  lines,
  accounts,
  glAccountId,
  onChange,
  loading,
}: {
  brokerage: ParsedBrokerageStatement;
  proposal: BrokerageProposal | null;
  lines: BrokerageProposedLine[];
  accounts: Account[];
  glAccountId: string;
  onChange: (next: BrokerageProposedLine[]) => void;
  loading: boolean;
}) {
  const unexplained = parseFloat(brokerage.unexplained);
  const update = (i: number, patch: Partial<BrokerageProposedLine>) =>
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const deposits = brokerage.components.find((c) => c.kind === 'deposits_withdrawals');

  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>Entries this statement calls for</h3>
      <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
        One entry per line of the statement&rsquo;s Change in NAV, dated{' '}
        {brokerage.header.statementDate
          ? formatAccountingDate(brokerage.header.statementDate)
          : 'the statement date'}
        , each against the investment account. Starting value{' '}
        {brokerage.header.startingValue ? formatCurrency(brokerage.header.startingValue) : '—'} →
        ending value{' '}
        {brokerage.header.endingValue ? formatCurrency(brokerage.header.endingValue) : '—'}.
        {deposits && (
          <>
            {' '}
            Deposits and withdrawals of {formatCurrency(deposits.amount, true)} are not posted
            here &mdash; the bank feed already did &mdash; they are matched below.
          </>
        )}
      </p>

      {Math.abs(unexplained) >= 0.005 && (
        <div className="field-error" style={{ marginBottom: 10 }}>
          The components read from the statement do not add up to its ending value: off by{' '}
          {formatCurrency(brokerage.unexplained, true)}. Check the amounts before posting.
        </div>
      )}

      {proposal && proposal.alreadyPosted.length > 0 && (
        <div className="card accent-risk" style={{ marginBottom: 10, padding: 10, fontSize: 13 }}>
          <strong>This statement was already posted</strong> &mdash;{' '}
          {proposal.alreadyPosted.map((p) => p.entryNumber).join(', ')}. Nothing will be posted
          again; those entries will be ticked on the reconciliation instead.
        </div>
      )}

      {loading && <div className="muted" style={{ fontSize: 13 }}>Working out the accounts…</div>}

      {lines.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Statement line</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ minWidth: 260 }}>Offset account</th>
              <th>Memo</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  {l.label}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {BROKERAGE_KIND_LABELS[l.kind]}
                    {l.lines.length > 0 && (
                      <details style={{ display: 'inline-block', marginLeft: 6 }}>
                        <summary style={{ display: 'inline', cursor: 'pointer' }}>
                          {l.lines.length} row{l.lines.length === 1 ? '' : 's'}
                        </summary>
                        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                          {l.lines.map((d, j) => (
                            <li key={j}>
                              {d.date ? formatAccountingDate(d.date) + ' · ' : ''}
                              {d.description} · {formatCurrency(d.amount, true)}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {formatCurrency(l.amount, true)}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {parseFloat(l.amount) >= 0 ? 'raises' : 'lowers'} the account
                  </div>
                </td>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId ?? ''}
                    onChange={(id) => update(i, { accountId: id || null, accountSource: 'map' })}
                    predicate={(a) => a.id !== glAccountId}
                    required
                  />
                  {l.accountSource === 'suggested' && l.accountId && (
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Guessed from the account name &mdash; confirm it.
                    </div>
                  )}
                  {!l.accountId && (
                    <div className="field-error" style={{ fontSize: 11, marginTop: 2 }}>
                      Choose where this posts.
                    </div>
                  )}
                </td>
                <td>
                  <input
                    value={l.memo}
                    onChange={(e) => update(i, { memo: e.target.value })}
                    style={{ width: '100%', minWidth: 180 }}
                    maxLength={500}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && proposal && lines.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>
          Nothing to post: every component on this statement is zero.
        </div>
      )}
    </div>
  );
}
